/**
 * @module components/marketing/CraftsIntro
 * @description "Two crafts, one standard of feedback" section introducing
 * the music and books analysis tracks. Static, presentational.
 *
 * @extracted-from client/src/App.js (lines ~1672-1709)
 */

import React from 'react';
import { Mic, BookOpen } from 'lucide-react';

const CraftsIntro = () => {
  return (
    <section className="mt-20 md:mt-24 scroll-mt-24" aria-labelledby="crafts-heading">
      <div className="critic-insight-line max-w-xs mx-auto mb-10" />
      <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12 px-2">
        <h3 id="crafts-heading" className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
          Two crafts, one standard of feedback
        </h3>
        <p className="mt-3 text-critic-text-secondary text-sm md:text-base leading-relaxed">
          Whether you are polishing a single or a manuscript, CriticAI applies the same idea: clear scores, structured notes, and market context — not empty praise.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <article className="group rounded-2xl border border-white/[0.08] bg-critic-bg-card/40 backdrop-blur-xl p-8 md:p-10 transition-all duration-critic ease-critic-out hover:border-apple-blue/35 hover:shadow-[0_0_0_1px_rgba(0,122,255,0.12)]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-apple-blue/30 to-critic-insight-mid/20 border border-white/[0.08] flex items-center justify-center">
              <Mic className="h-5 w-5 text-apple-blue" aria-hidden />
            </div>
            <h4 className="font-display text-lg font-semibold text-white">Music & audio</h4>
          </div>
          <p className="text-critic-text-secondary text-sm leading-relaxed mb-5">
            Waveform to feedback: production, arrangement, and how the track reads in the market. Upload MP3, WAV, or FLAC and add context with lyrics or instrumentals.
          </p>
          <div className="h-1 w-full rounded-full bg-gradient-to-r from-apple-blue/50 via-white/10 to-transparent opacity-80" aria-hidden />
        </article>
        <article className="group rounded-2xl border border-white/[0.08] bg-critic-bg-card/40 backdrop-blur-xl p-8 md:p-10 transition-all duration-critic ease-critic-out hover:border-critic-insight-purple/40 hover:glow-insight-ring">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-critic-insight-deep/35 to-apple-purple/20 border border-white/[0.08] flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-critic-insight-soft" aria-hidden />
            </div>
            <h4 className="font-display text-lg font-semibold text-white">Books & manuscripts</h4>
          </div>
          <p className="text-critic-text-secondary text-sm leading-relaxed mb-5">
            Margin to manuscript: structure, character, voice, and reader fit. Upload a document or paste text for a review that reads like an editorial pass, not a trend report.
          </p>
          <div className="h-1 w-full rounded-full bg-gradient-to-r from-critic-insight-purple/50 via-white/10 to-transparent opacity-80" aria-hidden />
        </article>
      </div>
    </section>
  );
};

export default CraftsIntro;
